import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit2, Trash2, X, Save, Loader2, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../Auth/AuthContext';

const emptyCustomer = { name: '', contact_person: '', email: '', phone: '', address: '', notes: '' };

export default function CustomersPage({ onSelectCustomer, selectMode = false }) {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null); // null = list, 'new' = ny kund, number = redigera
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await api.getCustomers();
      setCustomers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (editingId === 'new') {
        const newCustomer = await api.createCustomer(form);
        setCustomers(prev => [...prev, newCustomer]);
        if (selectMode && onSelectCustomer) onSelectCustomer(newCustomer);
      } else {
        const updated = await api.updateCustomer(editingId, form);
        setCustomers(prev => prev.map(c => c.id === editingId ? updated : c));
      }
      setEditingId(null);
      setForm(emptyCustomer);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort kunden? Befintliga offerter påverkas inte.')) return;
    try {
      await api.deleteCustomer(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const startEdit = (customer) => {
    setEditingId(customer.id);
    setForm({
      name: customer.name || '',
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_person || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (editingId !== null) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black">{editingId === 'new' ? 'Ny kund' : 'Redigera kund'}</h2>
          <button onClick={() => { setEditingId(null); setForm(emptyCustomer); setError(''); }} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase">Företag / Namn *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Kundnamn AB" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Kontaktperson</label>
              <input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Förnamn Efternamn" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">E-post</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="kund@foretag.se" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Telefon</label>
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Adress</label>
              <input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase">Anteckningar</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none" />
            </div>
          </div>
          {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
          <button type="submit" disabled={saving}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={18} /> Spara kund</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Users className="text-blue-600" /> Kunder
        </h2>
        <button
          onClick={() => { setEditingId('new'); setForm(emptyCustomer); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg"
        >
          <Plus size={18} /> Ny kund
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3.5 text-slate-400" size={18} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          placeholder="Sök kunder..."
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Users className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 font-bold">{search ? 'Inga kunder hittades' : 'Inga kunder ännu'}</p>
          {!search && <button onClick={() => setEditingId('new')} className="mt-4 text-blue-600 font-bold">+ Lägg till din första kund</button>}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(customer => (
            <div key={customer.id}
              className={`bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between group transition-all
                ${selectMode ? 'cursor-pointer hover:border-blue-400 hover:shadow-md' : ''}`}
              onClick={selectMode ? () => onSelectCustomer(customer) : undefined}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 text-blue-700 w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg flex-shrink-0">
                    {customer.name[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-black truncate">{customer.name}</p>
                    <p className="text-sm text-slate-500 truncate">
                      {customer.contact_person && <span>{customer.contact_person} · </span>}
                      {customer.email}
                    </p>
                  </div>
                </div>
              </div>
              {selectMode ? (
                <ChevronRight className="text-slate-400 flex-shrink-0 ml-2" size={20} />
              ) : (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                  <button onClick={() => startEdit(customer)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Edit2 size={16} />
                  </button>
                  {user?.role === 'admin' && (
                    <button onClick={() => handleDelete(customer.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
